import { Order, OrderItem, PackageDimensions, Product } from '../types';

// DHL CSV column headers (based on template)
export const DHL_CSV_HEADERS = [
  'Order Number',
  'Date',
  'To Name', 
  'Destination Building',
  'Destination Street',
  'Destination Suburb',
  'Destination City',
  'Destination Postcode',
  'Destination State',
  'Destination Country',
  'Destination Email',
  'Destination Phone',
  'Item Name',
  'Item Price',
  'Instructions',
  'Weight',
  'Shipping Method',
  'Reference',
  'SKU',
  'Qty',
  'Company',
  'Signature Required',
  'ATL',
  'Country Code',
  'Package Height',
  'Package Width', 
  'Package Length',
  'Carrier',
  'Carrier Product Code',
  'Carrier Product Unit Type',
  'Declared Value Currency',
  'Code',
  'Color',
  'Size',
  'Contents',
  'Dangerous Goods',
  'Country of Manufacturer',
  'DDP',
  'ReceiverVAT',
  'ReceiverEORI',
  'ShippingFreightValue',
  'Brand',
  'Usage',
  'Material',
  'Model',
  'MID Code',
  'Receiver National ID',
  'Receiver Passport Number',
  'Receiver USCI',
  'Receiver CR',
  'Receiver Brazil CNP'
];

// Unit conversion functions
export const inchesToCm = (inches: number): number => {
  return Math.round(inches * 2.54 * 100) / 100; // Round to 2 decimal places
};

export const ouncesToKg = (ounces: number): number => {
  return Math.round(ounces * 0.0283495 * 100) / 100; // Round to 2 decimal places
};

// Currency conversion helpers using order's stored metadata
const getOrderCurrency = (order: Order) => {
  return order?.purchaseCurrency || 'USD';
};

const getOrderExchangeRates = (order: Order) => {
  return order?.exchangeRateSnapshot || { USD: 1, CAD: 1.35, GBP: 0.8 };
};

// Convert USD price to order's display currency using stored exchange rate
const getConvertedItemPriceFromOrder = (usdPrice: number, order: Order) => {
  const currency = getOrderCurrency(order);
  const rates = getOrderExchangeRates(order);
  
  if (currency === 'USD') {
    return usdPrice;
  }
  
  const rate = rates[currency] || 1;
  return Math.ceil(usdPrice * rate);
};

// Helper function to get effective price (sale price if on sale, otherwise regular price)
const getEffectivePrice = (item: OrderItem) => {
  if (item.onSale && item.onSalePrice && item.onSalePrice < item.price) {
    return item.onSalePrice;
  }
  return item.price;
};

// Helper function to clean item name - remove brackets and their contents
const cleanItemName = (name: string): string => {
  // Remove anything within brackets/parentheses and the brackets themselves
  return name.replace(/\s*[\[\(].*?[\]\)]\s*/g, '').trim();
};

// Helper function to format enhanced item name with brand, cleaned name, and weight
const formatEnhancedItemName = (item: OrderItem, product?: Product): string => {
  const cleanedName = cleanItemName(item.name);
  const brand = product?.brand || '';
  const weight = product?.netWeight || '';
  const weightUnit = product?.weightUnit || '';
  
  let formattedName = cleanedName;
  
  // Add brand if available
  if (brand) {
    formattedName = `${brand} ${formattedName}`;
  }
  
  // Add weight if available
  if (weight && weightUnit) {
    formattedName = `${formattedName} ${weight}${weightUnit}`;
  }
  
  return formattedName;
};

// Helper function to calculate unit price with currency conversion
const calculateUnitPrice = (item: OrderItem, order: Order, product?: Product): number => {
  const effectivePrice = getEffectivePrice(item);
  const listingQuantity = product?.listingQuantity || 1;
  
  // For bundle items (listing quantity > 1), convert currency first, then divide and round
  if (listingQuantity > 1) {
    const convertedBundlePrice = getConvertedItemPriceFromOrder(effectivePrice, order);
    const unitPrice = convertedBundlePrice / listingQuantity;
    return Math.round(unitPrice * 100) / 100; // Round to 2 decimal places for bundle items
  }
  
  // For regular items, return the effective price (currency conversion happens later)
  return effectivePrice;
};

// Helper function to calculate total quantity (order quantity × listing quantity)
const calculateTotalQuantity = (item: OrderItem, product?: Product): number => {
  const orderQuantity = item.quantity;
  const listingQuantity = product?.listingQuantity || 1;
  return orderQuantity * listingQuantity;
};


// Helper function to convert weight to kg based on weight unit
const convertWeightToKg = (weight: number, unit: string): number => {
  switch (unit?.toLowerCase()) {
    case 'kg':
    case 'kilogram':
    case 'kilograms':
      return weight;
    case 'g':
    case 'gram':
    case 'grams':
      return weight / 1000;
    case 'oz':
    case 'ounce':
    case 'ounces':
      return ouncesToKg(weight);
    case 'lb':
    case 'lbs':
    case 'pound':
    case 'pounds':
      return weight * 0.453592; // Convert pounds to kg
    default:
      // Assume grams if unit is unknown/empty
      return weight / 1000;
  }
};


// Format date as DD/MM/YYYY for DHL
const formatDhlDate = (date: Date): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Get country code from country name (basic mapping)
const getCountryCode = (country: string): string => {
  const countryCodeMap: { [key: string]: string } = {
    'United States': 'US',
    'USA': 'US',
    'Canada': 'CA',
    'United Kingdom': 'GB',
    'UK': 'GB',
    'Australia': 'AU',
    'New Zealand': 'NZ',
    'Germany': 'DE',
    'France': 'FR',
    'Italy': 'IT',
    'Spain': 'ES',
    'Netherlands': 'NL',
    'Belgium': 'BE',
    'Switzerland': 'CH',
    'Austria': 'AT',
    'Sweden': 'SE',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Brazil': 'BR',
    'Mexico': 'MX',
    'Japan': 'JP',
    'South Korea': 'KR',
    'China': 'CN',
    'India': 'IN',
    'Singapore': 'SG',
    'Hong Kong': 'HK',
    'Malaysia': 'MY',
    'Thailand': 'TH',
    'Philippines': 'PH',
    'Indonesia': 'ID',
    'Vietnam': 'VN',
    'South Africa': 'ZA',
    'Israel': 'IL',
    'United Arab Emirates': 'AE',
    'UAE': 'AE',
    'Saudi Arabia': 'SA',
    'Turkey': 'TR',
    'Russia': 'RU',
    'Poland': 'PL',
    'Czech Republic': 'CZ',
    'Hungary': 'HU',
    'Romania': 'RO',
    'Bulgaria': 'BG',
    'Croatia': 'HR',
    'Greece': 'GR',
    'Portugal': 'PT',
    'Ireland': 'IE',
    'Lithuania': 'LT',
    'Latvia': 'LV',
    'Estonia': 'EE',
    'Slovenia': 'SI',
    'Slovakia': 'SK'
  };
  
  return countryCodeMap[country] || country;
};

// Interface for DHL CSV row data
export interface DhlCsvRowData {
  orderNumber: string;
  date: string;
  toName: string;
  destinationBuilding: string;
  destinationStreet: string;
  destinationSuburb: string;
  destinationCity: string;
  destinationPostcode: string;
  destinationState: string;
  destinationCountry: string;
  destinationEmail: string;
  destinationPhone: string;
  itemName: string;
  itemPrice: string;
  instructions: string;
  weight: string;
  shippingMethod: string;
  reference: string;
  sku: string;
  qty: string;
  company: string;
  signatureRequired: string;
  atl: string;
  countryCode: string;
  packageHeight: string;
  packageWidth: string;
  packageLength: string;
  carrier: string;
  carrierProductCode: string;
  carrierProductUnitType: string;
  declaredValueCurrency: string;
  code: string;
  color: string;
  size: string;
  contents: string;
  dangerousGoods: string;
  countryOfManufacturer: string;
  ddp: string;
  receiverVAT: string;
  receiverEORI: string;
  shippingFreightValue: string;
  brand: string;
  usage: string;
  material: string;
  model: string;
  midCode: string;
  receiverNationalId: string;
  receiverPassportNumber: string;
  receiverUSCI: string;
  receiverCR: string;
  receiverBrazilCNP: string;
}

// Enhanced product data for CSV generation
export interface EnhancedProductData {
  [itemId: string]: Product;
}

// Generate DHL CSV data from order
export const generateDhlCsvData = (
  order: Order, 
  packageDimensions: PackageDimensions,
  productData: EnhancedProductData = {},
  overrides: Partial<DhlCsvRowData> = {}
): DhlCsvRowData[] => {
  const baseData: Omit<DhlCsvRowData, 'itemName' | 'itemPrice' | 'sku' | 'qty' | 'code' | 'color' | 'size' | 'contents' | 'brand' | 'material' | 'model' | 'countryOfManufacturer'> = {
    orderNumber: order.orderNumber || order.id,
    date: formatDhlDate(order.createdAt),
    toName: order.customer.name,
    destinationBuilding: order.customer.address?.line2 || '',
    destinationStreet: order.customer.address?.line1 || '',
    destinationSuburb: order.customer.address?.city || '', // Use city as suburb fallback
    destinationCity: order.customer.address?.city || '',
    destinationPostcode: order.customer.address?.postal_code || '',
    destinationState: order.customer.address?.state || '',
    destinationCountry: order.customer.address?.country || '',
    destinationEmail: order.customer.email || '',
    destinationPhone: order.customer.phone || '',
    instructions: overrides.instructions || '',
    weight: '0', // Will be set per item based on individual product weight
    shippingMethod: overrides.shippingMethod || 'DHL Express Intl',
    reference: overrides.reference || order.id,
    company: overrides.company || '', // Always blank as requested
    signatureRequired: overrides.signatureRequired || 'No', // Default to No as requested
    atl: overrides.atl || 'Yes', // Default to Yes as requested
    countryCode: getCountryCode(order.customer.address?.country || ''),
    packageHeight: inchesToCm(packageDimensions.height).toString(),
    packageWidth: inchesToCm(packageDimensions.width).toString(), 
    packageLength: inchesToCm(packageDimensions.length).toString(),
    carrier: overrides.carrier || 'DHL',
    carrierProductCode: overrides.carrierProductCode || 'WPX',
    carrierProductUnitType: overrides.carrierProductUnitType || 'Box',
    declaredValueCurrency: getOrderCurrency(order),
    dangerousGoods: overrides.dangerousGoods || 'N',
    ddp: overrides.ddp || 'N',
    receiverVAT: overrides.receiverVAT || '',
    receiverEORI: overrides.receiverEORI || '',
    shippingFreightValue: overrides.shippingFreightValue || '',
    usage: overrides.usage || '',
    midCode: overrides.midCode || '',
    receiverNationalId: overrides.receiverNationalId || '',
    receiverPassportNumber: overrides.receiverPassportNumber || '',
    receiverUSCI: overrides.receiverUSCI || '',
    receiverCR: overrides.receiverCR || '',
    receiverBrazilCNP: overrides.receiverBrazilCNP || ''
  };

  // Generate one row per item (DHL requirement for multi-item orders)
  return order.items.map((item, index) => {
    const product = productData[item.id];
    
    // Calculate unit price with currency conversion
    const unitPrice = calculateUnitPrice(item, order, product);
    const listingQuantity = product?.listingQuantity || 1;
    
    // For regular items, apply currency conversion; for bundles, it's already converted
    const convertedUnitPrice = listingQuantity > 1 
      ? unitPrice  // Already converted and rounded for bundles
      : getConvertedItemPriceFromOrder(unitPrice, order); // Convert for regular items
    
    // Calculate total quantity (order quantity × listing quantity)
    const totalQuantity = calculateTotalQuantity(item, product);
    
    // Get country of manufacturer from product's origin country, default to 'JM'
    const countryOfManufacturer = overrides.countryOfManufacturer || 
                                  product?.originCountry || 
                                  'JM';
    
    // Calculate individual product weight in kg
    const productWeight = product?.netWeight || 0;
    const weightUnit = product?.weightUnit || 'g';
    const productWeightInKg = convertWeightToKg(productWeight, weightUnit);
    
    return {
      ...baseData,
      countryOfManufacturer, // Override the base data with item-specific country
      weight: productWeightInKg.toString(), // Override with individual product weight in kg
      itemName: formatEnhancedItemName(item, product),
      itemPrice: convertedUnitPrice.toString(),
      sku: overrides.sku || item.id,
      qty: totalQuantity.toString(),
      code: overrides.code || product?.hsCode || '',
      color: overrides.color || '',
      size: overrides.size || '',
      contents: overrides.contents || '',
      brand: overrides.brand || product?.brand || '',
      material: overrides.material || '',
      model: overrides.model || ''
    };
  });
};

// Convert DHL CSV data to CSV string
export const convertToCsvString = (data: DhlCsvRowData[]): string => {
  if (data.length === 0) return '';
  
  // Create CSV content
  const headers = DHL_CSV_HEADERS.join(',');
  const rows = data.map(row => {
    return [
      row.orderNumber,
      row.date,
      row.toName,
      row.destinationBuilding,
      row.destinationStreet,
      row.destinationSuburb,
      row.destinationCity,
      row.destinationPostcode,
      row.destinationState,
      row.destinationCountry,
      row.destinationEmail,
      row.destinationPhone,
      row.itemName,
      row.itemPrice,
      row.instructions,
      row.weight,
      row.shippingMethod,
      row.reference,
      row.sku,
      row.qty,
      row.company,
      row.signatureRequired,
      row.atl,
      row.countryCode,
      row.packageHeight,
      row.packageWidth,
      row.packageLength,
      row.carrier,
      row.carrierProductCode,
      row.carrierProductUnitType,
      row.declaredValueCurrency,
      row.code,
      row.color,
      row.size,
      row.contents,
      row.dangerousGoods,
      row.countryOfManufacturer,
      row.ddp,
      row.receiverVAT,
      row.receiverEORI,
      row.shippingFreightValue,
      row.brand,
      row.usage,
      row.material,
      row.model,
      row.midCode,
      row.receiverNationalId,
      row.receiverPassportNumber,
      row.receiverUSCI,
      row.receiverCR,
      row.receiverBrazilCNP
    ].map(field => {
      // Escape fields that contain commas, quotes, or newlines
      const stringField = String(field || '');
      if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
        return `"${stringField.replace(/"/g, '""')}"`;
      }
      return stringField;
    }).join(',');
  });

  return [headers, ...rows].join('\n');
};

// Download CSV file
export const downloadCsvFile = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  URL.revokeObjectURL(url);
};

// Generate filename for DHL CSV export
export const generateDhlCsvFilename = (order: Order): string => {
  const orderNumber = order.orderNumber || order.id;
  const date = formatDhlDate(order.createdAt).replace(/\//g, '-');
  return `DHL_Export_${orderNumber}_${date}.csv`;
};